import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';

export interface TenantWorkspace {
    id: string;
    name: string;
    slug: string;
}

export interface User {
    id: string;
    email: string;
    role: string;
    roles: string[];
    permissions: string[];
    tenantId: string;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    employeeId?: string; // set for Employee role users
    departmentId?: string; // set for Department Head role users
    branchId?: string; // set for Branch Admin role users
    dependents?: any[];
    clearanceLevel?: number;
    displayTimezone?: string;
    availableTenants?: TenantWorkspace[];
    isMasterAdmin?: boolean;
    countryCode?: string;
}

interface AuthState {
    user: User | null;
    accessToken: string | null;
    isAuthenticated: boolean;
    viewMode: 'ESS' | 'ADMIN';
    availableTenants: TenantWorkspace[];
}

const getInitialState = (): AuthState => {
    try {
        const token = localStorage.getItem('token');
        const userStr = localStorage.getItem('user');
        if (token && userStr) {
            const user = JSON.parse(userStr);
            // 10 (least privilege), never 1 — on this scale 1 is the super-admin
            // tier, so a stored user without the field must not be restored as
            // an admin session.
            const clearanceLevel = user.clearanceLevel || user.clearance_level || 10;
            const userRole = (user.role || '').toLowerCase();
            const isAdmin = ['super_admin', 'admin', 'hr_admin', 'hr', 'manager'].includes(userRole);
            const fullName = user.fullName || user.full_name || '';

            return {
                user: {
                    ...user,
                    fullName,
                    // Same derivation as setCredentials — repeated here so sessions
                    // that logged in before this fix still show a real name after
                    // a page reload, without forcing a re-login.
                    firstName: user.firstName || user.first_name || fullName.split(' ')[0] || undefined,
                    lastName: user.lastName || user.last_name || fullName.split(' ').slice(1).join(' ') || undefined,
                    displayTimezone: user.displayTimezone || user.display_timezone || 'UTC'
                },
                accessToken: token,
                isAuthenticated: true,
                // Default: Level 1-5 or explicit Admin roles = ADMIN, others = ESS
                viewMode: (clearanceLevel > 0 && clearanceLevel <= 5) || isAdmin ? 'ADMIN' : 'ESS',
                availableTenants: user.availableTenants || user.available_tenants || []
            };
        }
    } catch (e) {
        console.error('Failed to rehydrate auth state', e);
    }
    return {
        user: null,
        accessToken: null,
        isAuthenticated: false,
        viewMode: 'ESS',
        availableTenants: []
    };
};

const initialState: AuthState = getInitialState();

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setCredentials: (
            state,
            action: PayloadAction<{ user: any; accessToken: string }>
        ) => {
            const { user, accessToken } = action.payload;
            const fullName = user.fullName || user.full_name || '';
            // Map common snake_case to camelCase if needed
            const normalizedUser = {
                ...user,
                clearanceLevel: user.clearanceLevel || user.clearance_level,
                tenantId: user.tenantId || user.tenant_id,
                employeeId: user.employeeId || user.employee_id,
                departmentId: user.departmentId || user.department_id,
                branchId: user.branchId || user.branch_id,
                fullName,
                // Login responses only ever send fullName, never separate first/last
                // fields — derive them here so every UI that greets/initials the
                // user (ESSDashboard, EmployeeNavbar, etc.) shows a real name
                // instead of silently falling back to the email/employee-ID prefix.
                firstName: user.firstName || user.first_name || fullName.split(' ')[0] || undefined,
                lastName: user.lastName || user.last_name || fullName.split(' ').slice(1).join(' ') || undefined,
                displayTimezone: user.displayTimezone || user.display_timezone || 'UTC',
                availableTenants: user.availableTenants || user.available_tenants || [],
                isMasterAdmin: user.isMasterAdmin || user.is_master_admin,
                countryCode: user.countryCode || user.country_code,
            };

            const userRole = (normalizedUser.role || '').toLowerCase();
            const isAdmin = ['super_admin', 'admin', 'hr_admin', 'hr', 'manager'].includes(userRole);

            state.user = normalizedUser;
            state.accessToken = accessToken;
            state.isAuthenticated = true;
            state.availableTenants = normalizedUser.availableTenants;
            // Standardizing Level 1-5 as "Management" for default Landing
            state.viewMode = (normalizedUser.clearanceLevel > 0 && normalizedUser.clearanceLevel <= 5) || isAdmin ? 'ADMIN' : 'ESS';

            localStorage.setItem('token', accessToken);
            localStorage.setItem('user', JSON.stringify(normalizedUser));
        },
        logOut: (state) => {
            state.user = null;
            state.accessToken = null;
            state.isAuthenticated = false;
            state.availableTenants = [];

            localStorage.removeItem('token');
            localStorage.removeItem('user');
        },
        toggleViewMode: (state) => {
            state.viewMode = state.viewMode === 'ADMIN' ? 'ESS' : 'ADMIN';
        },
        setViewMode: (state, action: PayloadAction<'ESS' | 'ADMIN'>) => {
            state.viewMode = action.payload;
        },
    },
});

export const { setCredentials, logOut, toggleViewMode, setViewMode } = authSlice.actions;

export default authSlice.reducer;

export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectCurrentToken = (state: RootState) => state.auth.accessToken;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectUserRole = (state: RootState) => state.auth.user?.role;
// Defaults to 10 (least privilege) — 1 is the super-admin tier.
export const selectUserClearance = (state: RootState) => state.auth.user?.clearanceLevel || 10;
export const selectViewMode = (state: RootState) => state.auth.viewMode;
export const selectAvailableTenants = (state: RootState) => state.auth.availableTenants;
export const selectIsMasterAdmin = (state: RootState) => !!state.auth.user?.isMasterAdmin;
export const selectCurrentCountryCode = (state: RootState) => state.auth.user?.countryCode;
